import { useMemo } from 'react'
import z from 'zod'
import { useForm, zodResolver } from '@mantine/form'
import { useSelectedDenom } from '@/contexts/denom'
import { useSelectedChainBalance, useStrideChainBalance } from '@/contexts/wallet'
import { useStake } from '../StakeProvider'
import { useLsmValidatorsQuery } from '../queries'
import { CHAIN_CONFIG, CHAIN_INFO_LIST } from '@/config'
import { convertMicroDenomToDenom } from '@/wallet-utils'

export interface StakeFormType {
  defaultAmount: string
  lsmAmount: string
}

const useStakeForm = () => {
  const [denom] = useSelectedDenom()

  const { data: selectedBalances } = useSelectedChainBalance()

  const chainInfo = CHAIN_INFO_LIST[denom]

  const chainBalance = BigInt(selectedBalances?.balance?.amount || '0')

  const { data: strideBalances } = useStrideChainBalance()

  const chainBalanceOnStride = BigInt(strideBalances?.balance?.amount || '0')

  const totalBalance = chainBalance + chainBalanceOnStride

  const { mode, lsm } = useStake()

  const { data: lsmValidators } = useLsmValidatorsQuery()

  const selectedValidator = useMemo(() => {
    return lsmValidators?.validators.find((validator) => {
      return validator.address === lsm.validatorAddress
    })
  }, [lsm, lsmValidators])

  const schema = useMemo(() => {
    if (mode === 'default') {
      const totalBalanceInDenom = Number(
        convertMicroDenomToDenom(totalBalance, chainInfo.stakeCurrency.coinMinimalDenom)
      )

      const minimum = CHAIN_CONFIG[denom].classicLsMinimum

      return z.object({
        // @TODO: Maybe we should add a validation for the decimal precision so
        // they don't manually enter something that would fail the ibc like so: 0.02310230985
        defaultAmount: z.preprocess(
          (v) => Number(z.string().parse(v)),
          z
            .number({
              required_error: 'Amount is required.',
              invalid_type_error: 'Amount must be a number.'
            })
            .min(minimum, `Amount must have a value more than ${minimum}.`)
            .max(totalBalanceInDenom, 'Amount exceeds your balance.')
        )
      })
    }

    const getMaxRules = () => {
      if (selectedValidator == null) {
        return { amount: 0, message: 'Amount exceeds your balance.' }
      }

      const validatorStakedBalance = Number(convertMicroDenomToDenom(selectedValidator.amount))

      return { amount: validatorStakedBalance, message: `Amount exceeds your balance.` }
    }

    const max = getMaxRules()

    return z.object({
      // @TODO: Maybe we should add a validation for the decimal precision so
      // they don't manually enter something that would fail the ibc like so: 0.02310230985
      lsmAmount: z.preprocess(
        (v) => Number(z.string().parse(v)),
        z
          .number({
            required_error: 'Amount is required.',
            invalid_type_error: 'Amount must be a number.'
          })
          .min(0.001, 'Amount must have a value more than 0.001.')
          .max(max.amount, max.message)
      )
    })
  }, [denom, mode, totalBalance, selectedValidator])

  return useForm<StakeFormType>({
    schema: zodResolver(schema),
    initialValues: {
      defaultAmount: '',
      lsmAmount: ''
    }
  })
}

export { useStakeForm }
